import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Divider, Text
} from 'components/ui';
import DefaultPaymentCheckbox from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo/DefaultPaymentCheckbox';
import {
    colors, fontSizes, fontWeights, lineHeights, mediaQueries, space
} from 'style/config';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import PayPalCheckout from 'utils/PayPalCheckout';

const PayPalExpandedView = ({
    paypalHeader = 'Pay with PayPal',
    paypalLegalNotice,
    paypalSaveAndContinue = 'Save and Continue',
    paypalCancel = 'Cancel',
    onSaveAndContinue,
    onCancel,
    isAnonymous,
    onDefaultPaymentMethodSelected
}) => {
    const headerRef = useRef(null);

    useEffect(() => {
        headerRef.current?.focus();

        FrictionlessCheckoutBindings.setAlternatePaymentExpandedViewAnalytics('paypal');
    }, []);

    return (
        <Box
            role='region'
            aria-labelledby='paypal-expanded-header'
            data-testid='paypal-expanded-view'
        >
            <Text
                as='h3'
                id='paypal-expanded-header'
                ref={headerRef}
                tabIndex={-1}
                css={styles.header}
            >
                {paypalHeader}
            </Text>
            {!isAnonymous && PayPalCheckout.isSetPayPalAsDefaultPaymentEnabled() && (
                <Box css={styles.checkboxContainer}>
                    <DefaultPaymentCheckbox
                        paymentName='PAY_PAL'
                        useSelectedDefaultOnly
                        onToggle={action => onDefaultPaymentMethodSelected(action)}
                    />
                </Box>
            )}
            {paypalLegalNotice && (
                <Text
                    is='p'
                    css={styles.legalNotice}
                    dangerouslySetInnerHTML={{ __html: paypalLegalNotice }}
                />
            )}
            <Divider css={styles.divider} />
            <Box css={styles.buttonContainer}>
                <Button
                    variant='secondary'
                    css={styles.cancelButton}
                    onClick={onCancel}
                    data-testid='paypal-cancel'
                    aria-label='Cancel PayPal selection and return'
                >
                    {paypalCancel}
                </Button>
                <Button
                    variant='primary'
                    css={styles.saveButton}
                    onClick={onSaveAndContinue}
                    data-testid='paypal-save-continue'
                    aria-label='Save PayPal and return to payment options'
                >
                    {paypalSaveAndContinue}
                </Button>
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.base,
        marginTop: space[3],
        marginBottom: space[1]
    },
    checkboxContainer: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    legalNotice: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        lineHeight: lineHeights.base,
        marginBottom: space[3],
        marginTop: space[2],
        '& a': {
            textDecoration: 'underline',
            color: colors.blue
        }
    },
    divider: {
        marginTop: space[3],
        marginBottom: space[3],
        borderColor: colors.lightGray,
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block'
        }
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: space[2],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            gap: space[3]
        }
    },
    cancelButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    },
    saveButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    }
};

PayPalExpandedView.propTypes = {
    paypalHeader: PropTypes.string,
    paypalLegalNotice: PropTypes.string,
    paypalSaveAndContinue: PropTypes.string,
    paypalCancel: PropTypes.string,
    onSaveAndContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isAnonymous: PropTypes.bool,
    onDefaultPaymentMethodSelected: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(PayPalExpandedView, 'PayPalExpandedView');
